import { useMutation, useQuery } from '@apollo/client'
import { ALL_FIXED_SALARY_DAY_ADDONS } from './queries'
import { useConfirm, useNodes, usePermissions } from '@/utils/hooks'
import { OldAddButton } from '@/modules/companies'
import { Table } from '@/components_deprecated/Table'
import { formatISO } from 'date-fns'
import styled from 'styled-components'
import {
  Icon,
  Loader,
  usePrompt,
  useToast,
} from '@ur-solutions/react-components'
import { Card } from '@/components_deprecated/Card'
import { isMobileOnly } from 'react-device-detect'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { OverrideOrAddonForm } from './types'
import { EditAddonPrompt } from './EditAddonPrompt'
import type { FixedHourlyAddonNode } from './types.graphql'
import {
  CREATE_FIXED_SALARY_DAY_ADDON,
  DELETE_FIXED_SALARY_DAY_ADDON,
  PATCH_FIXED_SALARY_DAY_ADDON,
} from './mutations'
import { PERMISSIONS } from '@/utils/permissions'
import { H3 } from '@/components'

const Wrapper = styled(Card)`
  header {
    grid-area: header;

    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
      margin: 0;
      font-size: 1.6rem;
    }
    div {
      display: flex;

      button + button {
        margin-left: 1rem;
      }
    }
  }
`

interface FixedSalaryDayAddonsProps {
  typeId: string
  type: 'user' | 'salaryWorkTime'
}

export const FixedSalaryDayAddons: React.JSX.CFC<FixedSalaryDayAddonsProps> = ({
  typeId,
  type,
}) => {
  const translations = useTranslate({
    fixedSalaryDayAddons: 'salary.salary-day-addons',
    overrideOtherAddons: 'salary.override-other-addons',

    start: 'common.start',
    end: 'common.end',
    salaryCode: 'common.salary-code',
    edit: 'common.edit',
    delete: 'common.delete',

    deleteTimeOverride: 'salary.delete-time-addon',
    deleteTimeOverrideConfirm: 'salary.delete-time-addon-confirm',

    toast: {
      successCreate: 'salary.time-addon-create-success',
      successUpdate: 'salary.time-addon-update-success',
      successDelete: 'salary.time-addon-delete-success',
    },
  })

  const addPrompt = usePrompt()
  const addToast = useToast()
  const confirm = useConfirm()
  const { hasPermissions } = usePermissions()

  const canAdd = hasPermissions(PERMISSIONS.salary.add.timeaddon, true)

  const canEdit = hasPermissions(PERMISSIONS.salary.change.timeaddon, true)
  const canDelete = hasPermissions(PERMISSIONS.salary.delete.timeaddon, true)

  const { data, loading } = useQuery(ALL_FIXED_SALARY_DAY_ADDONS, {
    variables: {
      [type]: typeId,
    },
  })

  const refetchQueries = [
    {
      query: ALL_FIXED_SALARY_DAY_ADDONS,
      variables: {
        [type]: typeId,
      },
    },
    'DriverActivityPeriodReport',
    'DriverActivityPeriodReportShallow',
    'AllDynamicColumnsQuery',
    'DriverActivityDayReport',
  ]

  const [createFixedSalaryDayAddon] = useMutation(
    CREATE_FIXED_SALARY_DAY_ADDON,
    {
      refetchQueries: refetchQueries,
      onCompleted: () => {
        addToast('success', translations.toast.successCreate)
      },
    }
  )

  const [patchFixedSalaryDayAddon] = useMutation(PATCH_FIXED_SALARY_DAY_ADDON, {
    refetchQueries: refetchQueries,
    onCompleted: () => {
      addToast('success', translations.toast.successUpdate)
    },
  })

  const [deleteFixedSalaryDayAddon] = useMutation(
    DELETE_FIXED_SALARY_DAY_ADDON,
    {
      refetchQueries: refetchQueries,
      onCompleted: () => {
        addToast('success', translations.toast.successDelete)
      },
    }
  )

  const fixedSalaryDayAddons = useNodes(data?.allFixedSalaryDayAddons)

  const handleAdd = async () => {
    const initialData = {
      salaryCode: null,
      comment: '',
    }
    const { data } = await addPrompt<OverrideOrAddonForm | null>(resolve => (
      <EditAddonPrompt
        initialData={initialData}
        onSubmit={resolve}
        isConfiguration={true}
      />
    ))

    if (!data) return

    let patchData = {}

    if (data.datetimeStart && data.datetimeEnd) {
      patchData = {
        datetimeStart: formatISO(data.datetimeStart),
        datetimeEnd: formatISO(data.datetimeEnd),
      }
    } else if (data.datetimeStart) {
      patchData = {
        datetimeStart: formatISO(data.datetimeStart),
      }
    } else if (data.datetimeEnd) {
      patchData = {
        datetimeEnd: formatISO(data.datetimeEnd),
      }
    }
    void createFixedSalaryDayAddon({
      variables: {
        input: {
          ...patchData,
          salaryCode: data.salaryCode ?? '',
          [type]: typeId,
        },
      },
    })
  }

  const handleEdit = async (addon: FixedHourlyAddonNode) => {
    const initialData = {
      salaryCode: addon.salaryCode?.id ?? '',
      comment: '',
    }
    const { data } = await addPrompt<OverrideOrAddonForm | null>(resolve => (
      <EditAddonPrompt
        initialData={initialData}
        onSubmit={resolve}
        isConfiguration={true}
      />
    ))

    if (!data) return

    let datetimeData = {}

    if (data.datetimeStart && data.datetimeEnd) {
      datetimeData = {
        datetimeStart: formatISO(data.datetimeStart),
        datetimeEnd: formatISO(data.datetimeEnd),
      }
    } else if (data.datetimeStart) {
      datetimeData = {
        datetimeStart: formatISO(data.datetimeStart),
      }
    } else if (data.datetimeEnd) {
      datetimeData = {
        datetimeEnd: formatISO(data.datetimeEnd),
      }
    }

    void patchFixedSalaryDayAddon({
      variables: {
        id: addon.id,
        input: {
          ...datetimeData,
          salaryCode: data.salaryCode,
        },
      },
    })
  }

  const handleDelete = async (addon: FixedHourlyAddonNode) => {
    const { data } = await confirm(
      translations.deleteTimeOverrideConfirm,
      translations.deleteTimeOverride
    )

    if (!data) return

    void deleteFixedSalaryDayAddon({
      variables: {
        id: addon.id,
      },
    })
  }

  const numUtilColumns = (canEdit ? 1 : 0) + (canDelete ? 1 : 0)

  return (
    <Wrapper>
      <header>
        <H3>{translations.fixedSalaryDayAddons}</H3>
        {loading && <Loader.Spinner />}
        {!loading && !isMobileOnly && canAdd && (
          <OldAddButton adding={false} onClick={handleAdd} />
        )}
      </header>
      {!loading && (
        <Table
          noShadow
          noBorders
          noHeaderMargin
          noRowMargin
          rowHoverColor="secondary"
        >
          <thead>
            <tr>
              <th>{translations.salaryCode}</th>
              {(canEdit || canDelete) && <th colSpan={numUtilColumns}></th>}
            </tr>
          </thead>
          <tbody>
            {fixedSalaryDayAddons.map(fixedSalaryDayAddon => (
              <tr key={fixedSalaryDayAddon.id}>
                <td>
                  {fixedSalaryDayAddon.salaryCode
                    ? fixedSalaryDayAddon.salaryCode.name
                    : '-'}
                </td>
                {canEdit && (
                  <td width="1px">
                    <Icon
                      icon="edit"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="secondary"
                      onClick={() => handleEdit(fixedSalaryDayAddon)}
                    />
                  </td>
                )}
                {canDelete && (
                  <td width="1px">
                    <Icon
                      icon="times"
                      cursor="pointer"
                      color="gray6"
                      hoverColor="red"
                      onClick={() => handleDelete(fixedSalaryDayAddon)}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Wrapper>
  )
}
